import type { EFormFieldSchema, EFormSchema, EFormSectionSchema } from './schema';

export type SchemaFieldDiffStatus = 'added' | 'removed' | 'changed' | 'unchanged';

export type SchemaFieldDiffEntry = {
  id: string;
  key: string;
  label: string;
  type: string;
  status: Exclude<SchemaFieldDiffStatus, 'unchanged'>;
  sectionHeading: string;
  /** Human-readable property names that differ (changed only). */
  changedProps?: string[];
};

export type EFormSchemaDiff = {
  added: SchemaFieldDiffEntry[];
  removed: SchemaFieldDiffEntry[];
  changed: SchemaFieldDiffEntry[];
  revisionFieldStatusById: Record<string, 'removed' | 'changed' | 'unchanged'>;
  revisionFieldStatusByKey: Record<string, 'removed' | 'changed' | 'unchanged'>;
  formMetaChanged: boolean;
  formMetaChanges: string[];
  totalChanges: number;
};

const FIELD_PROP_LABELS: Record<string, string> = {
  type: 'Type',
  key: 'Key',
  label: 'Label',
  required: 'Required',
  placeholder: 'Placeholder',
  helpText: 'Help text',
  options: 'Options',
  optionsSource: 'Options source',
  referenceKey: 'Reference',
  colSpan: 'Width',
  defaultValue: 'Default',
  validation: 'Validation',
  visibleWhen: 'Visibility',
  readOnly: 'Read-only',
  display: 'Display',
  calculatedFrom: 'Calculated from',
  multiple: 'Multiple files',
  accept: 'Accept',
  fields: 'Sub-form columns',
  minRows: 'Min rows',
  maxRows: 'Max rows',
  rowEditor: 'Row editor',
  section: 'Section',
};

const COMPARE_KEYS: (keyof EFormFieldSchema)[] = [
  'type',
  'key',
  'label',
  'required',
  'placeholder',
  'helpText',
  'options',
  'optionsSource',
  'referenceKey',
  'colSpan',
  'defaultValue',
  'validation',
  'visibleWhen',
  'readOnly',
  'display',
  'calculatedFrom',
  'multiple',
  'accept',
  'fields',
  'minRows',
  'maxRows',
  'rowEditor',
];

function stableStringify(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((v) => stableStringify(v)).join(',')}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

function fieldChangedProps(
  revisionField: EFormFieldSchema,
  draftField: EFormFieldSchema,
  sameSection: boolean,
): string[] {
  const changed: string[] = [];
  for (const key of COMPARE_KEYS) {
    if (stableStringify(revisionField[key]) !== stableStringify(draftField[key])) {
      changed.push(FIELD_PROP_LABELS[key] ?? key);
    }
  }
  if (!sameSection) changed.push(FIELD_PROP_LABELS.section);
  return changed;
}

type FieldLoc = {
  field: EFormFieldSchema;
  section: EFormSectionSchema;
};

function indexFields(schema: EFormSchema): Map<string, FieldLoc> {
  const map = new Map<string, FieldLoc>();
  for (const section of schema.sections ?? []) {
    for (const field of section.fields ?? []) {
      if (!field?.id) continue;
      map.set(field.id, { field, section });
    }
  }
  return map;
}

function entryFrom(
  loc: FieldLoc,
  status: Exclude<SchemaFieldDiffStatus, 'unchanged'>,
  changedProps?: string[],
): SchemaFieldDiffEntry {
  return {
    id: loc.field.id,
    key: loc.field.key,
    label: loc.field.label || loc.field.key || 'Untitled',
    type: loc.field.type,
    status,
    sectionHeading: loc.section.heading || 'Untitled section',
    changedProps,
  };
}

/**
 * Diff a revision schema against the current draft.
 * - added: in draft only
 * - removed: in revision only
 * - changed: same field id, different config/section
 */
export function diffEFormSchemas(
  revision: EFormSchema,
  draft: EFormSchema,
): EFormSchemaDiff {
  const revMap = indexFields(revision);
  const draftMap = indexFields(draft);

  const added: SchemaFieldDiffEntry[] = [];
  const removed: SchemaFieldDiffEntry[] = [];
  const changed: SchemaFieldDiffEntry[] = [];
  const revisionFieldStatusById: Record<string, 'removed' | 'changed' | 'unchanged'> = {};
  const revisionFieldStatusByKey: Record<string, 'removed' | 'changed' | 'unchanged'> = {};

  for (const [id, revLoc] of Array.from(revMap.entries())) {
    const draftLoc = draftMap.get(id);
    if (!draftLoc) {
      removed.push(entryFrom(revLoc, 'removed'));
      revisionFieldStatusById[id] = 'removed';
      revisionFieldStatusByKey[revLoc.field.key] = 'removed';
      continue;
    }
    const props = fieldChangedProps(
      revLoc.field,
      draftLoc.field,
      revLoc.section.id === draftLoc.section.id,
    );
    if (props.length > 0) {
      changed.push(entryFrom(revLoc, 'changed', props));
      revisionFieldStatusById[id] = 'changed';
      revisionFieldStatusByKey[revLoc.field.key] = 'changed';
    } else {
      revisionFieldStatusById[id] = 'unchanged';
      revisionFieldStatusByKey[revLoc.field.key] = 'unchanged';
    }
  }

  for (const [id, draftLoc] of Array.from(draftMap.entries())) {
    if (!revMap.has(id)) {
      added.push(entryFrom(draftLoc, 'added'));
    }
  }

  const formMetaChanges: string[] = [];
  if ((revision.instructions ?? '') !== (draft.instructions ?? '')) {
    formMetaChanges.push('Instructions');
  }
  const revSettings = revision.settings ?? {};
  const draftSettings = draft.settings ?? {};
  if (Boolean(revSettings.requireApproval) !== Boolean(draftSettings.requireApproval)) {
    formMetaChanges.push('Require approval');
  }
  if ((revSettings.approvalTemplateCode ?? '') !== (draftSettings.approvalTemplateCode ?? '')) {
    formMetaChanges.push('Approval template');
  }
  if (stableStringify(revSettings.listColumns) !== stableStringify(draftSettings.listColumns)) {
    formMetaChanges.push('List columns');
  }
  if (stableStringify(revSettings.filterFields) !== stableStringify(draftSettings.filterFields)) {
    formMetaChanges.push('List filters');
  }
  if (stableStringify(revSettings.sidebarPlacements) !== stableStringify(draftSettings.sidebarPlacements)) {
    formMetaChanges.push('Sidebar placement');
  }
  if (stableStringify(revSettings.access) !== stableStringify(draftSettings.access)) {
    formMetaChanges.push('Access');
  }

  // Section structure (heading / columns) for sections that still exist
  const draftSections = new Map((draft.sections ?? []).map((s) => [s.id, s]));
  for (const section of revision.sections ?? []) {
    const other = draftSections.get(section.id);
    if (!other) {
      formMetaChanges.push(`Section removed: ${section.heading || 'Untitled'}`);
      continue;
    }
    if ((section.heading ?? '') !== (other.heading ?? '')) {
      formMetaChanges.push(`Section title: ${section.heading || 'Untitled'}`);
    }
    if ((section.subHeading ?? '') !== (other.subHeading ?? '')) {
      formMetaChanges.push(`Section subtitle: ${section.heading || 'Untitled'}`);
    }
    if ((section.columns ?? 2) !== (other.columns ?? 2)) {
      formMetaChanges.push(`Section columns: ${section.heading || 'Untitled'}`);
    }
  }
  for (const section of draft.sections ?? []) {
    if (!(revision.sections ?? []).some((s) => s.id === section.id)) {
      formMetaChanges.push(`Section added: ${section.heading || 'Untitled'}`);
    }
  }

  const formMetaChanged = formMetaChanges.length > 0;
  const totalChanges = added.length + removed.length + changed.length + (formMetaChanged ? 1 : 0);

  return {
    added,
    removed,
    changed,
    revisionFieldStatusById,
    revisionFieldStatusByKey,
    formMetaChanged,
    formMetaChanges,
    totalChanges,
  };
}

/** Convenience: empty diff when schemas are unavailable. */
export function emptyEFormSchemaDiff(): EFormSchemaDiff {
  return {
    added: [],
    removed: [],
    changed: [],
    revisionFieldStatusById: {},
    revisionFieldStatusByKey: {},
    formMetaChanged: false,
    formMetaChanges: [],
    totalChanges: 0,
  };
}
